/**
 * DocumentRenderer — renders a `document` artifact (memo, guide, FAQ, brief,
 * summary, report).
 *
 * Wraps `<ArtifactFrame>` and renders the markdown body via the canonical
 * `MarkdownRenderer`. Computes a "N words · M min read" subtitle when the
 * artifact doesn't already provide one. Defaults to copy + download + the
 * three export actions (CSV / SVG / Print as PDF) — restored from the legacy
 * `DeliverableView` toolbar that lives in non-workspace mode. Exports are
 * handled inline (no backend roundtrip) by intercepting the `export.*`
 * action ids and operating on the rendered body's DOM.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useCallback, useMemo, useRef } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Artifact, ArtifactAction, DocumentPayload } from "@/api/models"
import type { TFunction } from "i18next"

import { ArtifactFrame } from "./ArtifactFrame"
import { WorkspaceMarkdownRenderer } from "./WorkspaceMarkdownRenderer"
import { downloadSvg, printDeliverableAsPdf } from "../../utils/exportDeliverable"
import { downloadCsv, markdownTableToCsv } from "../../utils/markdownTableToCsv"

// Always-on actions — every document supports these. Specific export
// actions (CSV / SVG) are conditionally appended based on body content
// (see `buildDocumentActions` below) so the overflow menu doesn't list
// formats the deliverable can't actually produce. The renderer owns these
// fallback labels, so it also owns translating them.
function buildBaseDocumentActions(t: TFunction): ArtifactAction[] {
  return [
    { id: "copy", label: t("deepExplorer.artifact_actions.copy"), icon: "copy" },
    { id: "download", label: t("deepExplorer.artifact_actions.download"), icon: "download" },
    // Reuses the existing canvas surface from normal Explorer — page-level
    // dispatcher in ai-explorer-page.tsx handles `edit_in_canvas` by calling
    // `createDocumentApi` + `openCanvasInSidebar` (mirrors system-message.tsx).
    {
      id: "edit_in_canvas",
      label: t("deepExplorer.artifact_actions.edit_in_canvas"),
      icon: "edit",
    },
    // PDF "print" works for any rendered body — always offer it.
    { id: "export.pdf", label: t("deepExplorer.artifact_actions.print_as_pdf"), icon: "download" },
  ]
}

/**
 * Detect whether the body contains a fenced Mermaid code block. The
 * MarkdownRenderer turns these into `<svg>` nodes at runtime; if no fence
 * exists, no SVG will be present to export, so we hide the action.
 */
function bodyHasMermaid(body: string): boolean {
  return /^```mermaid\b/m.test(body)
}

/**
 * Build the action list shown in the artifact frame. Static base + the
 * subset of exports the body can actually fulfil:
 * - CSV: only if the body contains a parseable markdown table.
 * - SVG: only if the body has a Mermaid fence (rendered to inline SVG).
 * - PDF: always available (browser-native print).
 * - copy / download / edit_in_canvas: always available.
 *
 * Honours an artifact's own `actions` array when it ships one (server-
 * provided actions win — same behavior as before).
 */
function buildDocumentActions(body: string, t: TFunction): ArtifactAction[] {
  const actions: ArtifactAction[] = buildBaseDocumentActions(t)
  if (markdownTableToCsv(body)) {
    actions.push({
      id: "export.csv",
      label: t("deepExplorer.artifact_actions.export_csv"),
      icon: "download",
    })
  }
  if (bodyHasMermaid(body)) {
    actions.push({
      id: "export.svg",
      label: t("deepExplorer.artifact_actions.export_svg"),
      icon: "download",
    })
  }
  return actions
}

function deriveExportFilename(title: string | undefined): string {
  // Mirror the legacy `DeliverableView` slug shape so a user who exports
  // from both surfaces gets matching filenames.
  const raw = (title ?? "").trim().toLowerCase()
  return (
    raw
      .replace(/[^\p{L}\p{N}]+/gu, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "deliverable"
  )
}

function formatWordCount(count: number): string {
  // Locale-stable thousands separator so "1,234 words" reads consistently in tests.
  return count.toLocaleString("en-US")
}

function buildDocumentSubtitle(payload: DocumentPayload): string | null {
  const wordCount = payload.word_count
  const readMinutes = payload.estimated_read_minutes
  if (!wordCount && !readMinutes) return null
  const parts: string[] = []
  if (wordCount) {
    parts.push(`${formatWordCount(wordCount)} word${wordCount === 1 ? "" : "s"}`)
  }
  if (readMinutes) {
    parts.push(`${readMinutes} min read`)
  }
  return parts.join(" · ")
}

export interface DocumentRendererProps {
  artifact: Artifact & { payload: DocumentPayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
  citationSection?: string
}

export function DocumentRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
  citationSection,
}: DocumentRendererProps) {
  const { t } = useTranslation()
  const bodyRef = useRef<HTMLDivElement>(null)

  const framedArtifact = useMemo<Artifact>(() => {
    // Compute subtitle from payload when the artifact didn't supply one;
    // fall back to default copy+download+export actions only when the
    // artifact explicitly shipped an empty `actions` array (or omitted them).
    const subtitle = artifact.subtitle ?? buildDocumentSubtitle(artifact.payload)
    const hasActions = (artifact.actions ?? []).length > 0
    const actions = hasActions ? artifact.actions : buildDocumentActions(artifact.payload.body, t)
    return { ...artifact, subtitle, actions }
  }, [artifact, t])

  // Intercept the three export action ids before they bubble to the parent.
  // Exports are inherently client-side (they operate on the rendered DOM
  // node, not server data), so a backend roundtrip would just add latency.
  // Bubble unrecognised ids unchanged so plan/ask-user/etc. dispatchers in
  // ai-explorer-page.tsx still work for any actions the artifact did ship.
  const handleAction = useCallback(
    (actionId: string, target: Artifact) => {
      const filename = deriveExportFilename(target.title)
      if (actionId === "export.csv") {
        const csv = markdownTableToCsv(artifact.payload.body)
        if (!csv) {
          toast.error(t("deepExplorer.export_csv_no_table"))
          return
        }
        downloadCsv(csv.csv, `${filename}.csv`)
        toast.success(t("deepExplorer.export_csv_success", { count: csv.rowCount }))
        return
      }
      if (actionId === "export.svg") {
        const ok = downloadSvg(bodyRef.current, `${filename}.svg`)
        if (ok) toast.success(t("deepExplorer.export_svg_success"))
        else toast.error(t("deepExplorer.export_svg_error"))
        return
      }
      if (actionId === "export.pdf") {
        const ok = printDeliverableAsPdf(bodyRef.current, filename)
        if (!ok) toast.error(t("deepExplorer.export_pdf_error"))
        return
      }
      onAction?.(actionId, target)
    },
    [artifact.payload.body, onAction, t]
  )

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={handleAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div ref={bodyRef} data-testid="document-renderer-body">
        <WorkspaceMarkdownRenderer
          citationSection={citationSection}
          content={artifact.payload.body}
        />
      </div>
    </ArtifactFrame>
  )
}
